/*
 * App Sagas
 *
 * Sagas are async side effects that handle complex logic and API calls
 */

import { call, put, takeLatest, all } from 'redux-saga/effects';
import request from '../../utils/request';
import { processCSVFile, processMultipleCSVFiles, generateBOM } from '../../utils/csvProcessor';
import { bomAPI } from '../../services/api';
import authService from '../../services/authService';
import userService from '../../services/userService';
import {
    INITIALIZE_SESSION_REQUEST,
    LOGOUT_SESSION_REQUEST,
    LOAD_BOM_DATA,
    SUBMIT_BOM_DATA,
} from './constants';
import {
    sessionInitialized,
    sessionInitializeFailed,
    bomDataLoaded,
    bomDataError,
    bomDataSubmitted,
    bomSubmitError,
} from './actions';

// Base host (can be overridden by env vars)
// For authentication and general API calls
// Development: http://localhost:18080
// Production: https://factory.servicetag.services.signify.com
export const apiHost = process.env.REACT_APP_API_URL || 'http://localhost:18080';

/**
 * Init Session request/response handler
 * 
 * Flow:
 * 1. App dispatches INITIALIZE_SESSION_REQUEST on mount
 * 2. Calls backend /api/idmauth endpoint with credentials (cookies)
 * 3. Backend validates session cookie and returns:
 *    - { isAuthenticated: true, user: {...} } if session valid
 *    - { isAuthenticated: false, loginUrl: "..." } if no session
 * 4. Success → dispatches sessionInitialized(session) → shows main app
 * 5. Need login → redirect to loginUrl
 */
export function* initializeSessionSaga() {
    try {
        // Call backend to validate session using authService
        const response = yield call(authService.initializeAuth);
        // Check if user is authenticated
        if (response && response.isAuthenticated === true && response.user) {
            // Try to get access token from session/user (if available)
            let userDetails = null;
            try {
                userDetails = yield call(userService.getUserDetails);
            } catch (e) {
                console.warn('Failed to fetch user details, continuing without it:', e);
                // Fallback: continue without userDetails
                userDetails = null;
            }
            yield put(sessionInitialized({
                user: response.user,
                isAuthenticated: true,
                userDetails,
            }));
        } else if (response && response.isAuthenticated === false && response.loginUrl) {
            // No valid session - redirect to login
            if (typeof window !== 'undefined' && window.location) {
                window.location.href = response.loginUrl;
            }
        } else {
            // Unexpected response
            yield put(sessionInitializeFailed('Invalid session response'));
        }

    } catch (err) {
        console.error('Session initialization error:', err);

        // On error, redirect to login
        if (err.status === 403 || err.status === 401) {
            // Not authenticated - redirect to login
            if (err.data && err.data.loginUrl) {
                window.location.href = err.data.loginUrl;
            } else {
                window.location.href = '/'; // Fallback to homepage if no login URL provided
            }
            return;
        }

        // Other errors
        yield put(sessionInitializeFailed(err.message || 'Session validation failed'));
    }
}

/**
 * Logout request/response handler
 * 
 * Flow:
 * 1. User clicks logout button
 * 2. Calls backend /api/idmauth/logout endpoint
 * 3. Backend destroys session and returns logout URL
 * 4. Redirect to logout URL (IdM logout page)
 * 5. After IdM logout, user is redirected back to app (unauthenticated)
 */
export function* logoutUserSaga() {
    try {
        const response = yield call(authService.logout);

        // Backend has destroyed session and returned logout URL
        if (response && response.logOutUrl) {
            // Redirect to IdM logout page
            if (typeof window !== 'undefined' && window.location) {
                window.location.href = response.logOutUrl;
            }
        } else {
            // No logout URL - just reload to clear session
            if (typeof window !== 'undefined' && window.location) {
                window.location.href = '/';
            }
        }

    } catch (error) {
        console.error('Logout error:', error);

        // Even if logout fails, clear local session and redirect
        yield put(sessionInitialized(null));

        // Reload page to clear any cached state
        if (typeof window !== 'undefined' && window.location) {
            window.location.href = '/';
        }
    }
}

/**
 * Load BOM data from CSV file(s)
 */
export function* loadBOMDataSaga(action) {
    try {
        const payload = action.payload;

        // Treat empty selection as an intentional clear, not an error.
        if (Array.isArray(payload) && payload.length === 0) {
            yield put(bomDataLoaded({
                bomData: {},
                csvData: null,
                message: null,
            }));
            return;
        }

        // Detect factory file format: servicetag column only or with extra columns
        // Skip data validation for factory files; let API validate and return invalid servicetags
        const isFactoryFileOnly = true; // When uploading factory files with servicetag column

        // Handle both single file and multiple files
        let processedData;
        if (Array.isArray(payload) && payload.length > 0) {
            // Multiple files
            processedData = yield call(processMultipleCSVFiles, payload, isFactoryFileOnly);
        } else if (!Array.isArray(payload)) {
            // Single file (backward compatibility)
            processedData = yield call(processCSVFile, payload, isFactoryFileOnly);
        } else {
            yield put(bomDataError('No files provided for processing'));
            return;
        }

        // Generate BOM - now using twelveNCs instead of batchIds
        const bom = yield call(generateBOM, processedData);

        const fileCount = Array.isArray(payload) ? payload.length : 1;
        const fileNames = Array.isArray(payload) 
            ? payload.map(f => f.name).join(', ')
            : payload.name;

        const manufacturingDateText = Array.isArray(processedData?.sourceFileManufacturingDates) && processedData.sourceFileManufacturingDates.length > 0
            ? ` Extracted manufacturing date(s): ${processedData.sourceFileManufacturingDates.join(', ')}.`
            : processedData?.sourceFileManufacturingDate
                ? ` Extracted manufacturing date: ${processedData.sourceFileManufacturingDate}.`
                : '';
        
        const message = `Successfully extracted ${processedData.twelveNCs?.length || Object.keys(bom).length} unique items from ${processedData.totalServiceTags} service tags (${fileCount} file(s): ${fileNames}).${manufacturingDateText}`;

        yield put(bomDataLoaded({
            bomData: bom,
            csvData: processedData,
            message,
        }));
    } catch (err) {
        yield put(bomDataError(`${err.message}`));
    }
}

/**
 * Submit BOM data to backend
 */
export function* submitBOMDataSaga(action) {
    try {
        const result = yield call(bomAPI.uploadBOM, action.payload);
        yield put(bomDataSubmitted(result));
    } catch (err) {
        yield put(bomSubmitError(`Failed to upload BOM data: ${err.message}`));
    }
}

// Watchers
function* watchInitializeSession() {
    yield takeLatest(INITIALIZE_SESSION_REQUEST, initializeSessionSaga);
}

function* watchLogoutSession() {
    yield takeLatest(LOGOUT_SESSION_REQUEST, logoutUserSaga);
}

function* watchLoadBOMData() {
    yield takeLatest(LOAD_BOM_DATA, loadBOMDataSaga);
}

function* watchSubmitBOMData() {
    yield takeLatest(SUBMIT_BOM_DATA, submitBOMDataSaga);
}

// Root saga
export default function* appSaga() {
    yield all([
        watchInitializeSession(),
        watchLogoutSession(),
        watchLoadBOMData(),
        watchSubmitBOMData(),
    ]);
}
